import { useState } from "react";
import { Camera, Video, Mic, ChevronDown, ChevronUp, Check, Phone, Mail } from "lucide-react";

const rentalServices = [
  {
    id: "photo-booth",
    title: "Photo Booth Rental",
    icon: Camera,
    description: "Capture all the fun moments with our interactive photo booth featuring photos, boomerangs, videos, and GIFs!",
    image: "https://static.wixstatic.com/media/3a96ea_3558a0d1546443d5b45ff6fbb510ce58~mv2.jpg/v1/fill/w_400,h_600,al_c,q_80,enc_avif,quality_auto/387327101_1113588916719015_7590251669268925818_n.jpg",
    packages: [
      {
        name: "Package 1",
        price: "$375",
        hours: "2 Hour Rental",
        features: [
          "Unlimited Captures",
          "ALL Capture Modes (Photos, Boomerangs, Video, GIF)",
          "Customized Start Screen",
          "Customized Photo Template",
          "Online Gallery",
          "Text & Email Sharing",
        ],
      },
      {
        name: "Package 2",
        price: "$425",
        hours: "3 Hour Rental",
        popular: true,
        features: [
          "Unlimited Captures",
          "ALL Capture Modes (Photos, Boomerangs, Video, GIF)",
          "Customized Start Screen",
          "Customized Photo Template",
          "Online Gallery",
          "Text & Email Sharing",
        ],
      },
      {
        name: "Package 3",
        price: "$500",
        hours: "4 Hour Rental",
        features: [
          "Unlimited Captures",
          "ALL Capture Modes (Photos, Boomerangs, Video, GIF)",
          "Customized Start Screen",
          "Customized Photo Template",
          "Online Gallery",
          "Text & Email Sharing",
        ],
      },
      {
        name: "Package 4",
        price: "$575",
        hours: "5 Hour Rental",
        features: [
          "Unlimited Captures",
          "ALL Capture Modes (Photos, Boomerangs, Video, GIF)",
          "Customized Start Screen",
          "Customized Photo Template",
          "Online Gallery",
          "Text & Email Sharing",
        ],
      },
    ],
    note: "Backdrop NOT included. A $50 non-refundable deposit is required to book.",
  },
  {
    id: "360-booth",
    title: "360 Booth Rental",
    icon: Video,
    description: "Step onto the platform and let the camera spin around you for stunning 360-degree videos perfect for any event!",
    image: "https://static.wixstatic.com/media/3a96ea_0bddf467e8894d8384af1e3352746cda~mv2.jpg/v1/fill/w_400,h_530,al_c,q_80,enc_avif,quality_auto/IMG_8466_edited.jpg",
    packages: [
      {
        name: "Package 1",
        price: "$400",
        hours: "2 Hour Rental",
        features: [
          "Online Gallery",
          "Text & Email Sharing",
        ],
      },
      {
        name: "Package 2",
        price: "$500",
        hours: "3 Hour Rental",
        popular: true,
        features: [
          "Online Gallery",
          "Text & Email Sharing",
        ],
      },
      {
        name: "Package 3",
        price: "$600",
        hours: "4 Hour Rental",
        features: [
          "Online Gallery",
          "Text & Email Sharing",
        ],
      },
      {
        name: "Package 4",
        price: "$700",
        hours: "5 Hour Rental",
        features: [
          "Online Gallery",
          "Text & Email Sharing",
        ],
      },
    ],
    note: "Backdrop NOT included. Travel fee may be added.",
  },
  {
    id: "audio-guest-book",
    title: "Audio Guest Book Rental",
    icon: Mic,
    description: "A revolutionary way for your guests to leave heartfelt voice messages that will be cherished for years to come. Capture the voices and emotions of your loved ones!",
    image: "https://static.wixstatic.com/media/3a96ea_f6023657d2b041be9f1348ebe210c607~mv2.jpg/v1/fill/w_400,h_480,al_c,q_80,enc_avif,quality_auto/3a96ea_f6023657d2b041be9f1348ebe210c607~mv2.jpg",
    packages: [
      {
        name: "Full Event Rental",
        price: "$250",
        hours: "Flexible Hours",
        popular: true,
        features: [
          "No set rental hours - drop off & pick up when convenient",
          "No limit on recordings",
          "Receive all recordings immediately via email",
          "Dropbox link included",
          "Voice messages never expire",
        ],
      },
    ],
    note: "Perfect for weddings, parties, and special events.",
  },
];

const BabeBoothSection = ({ isVisible }) => {
  const [expandedService, setExpandedService] = useState(null);

  const toggleService = (serviceId) => {
    setExpandedService(expandedService === serviceId ? null : serviceId);
  };

  return (
    <section
      id="rentals"
      className="py-24 lg:py-32 bg-[#F5F2EB]"
      data-testid="babe-booth-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div
          className={`text-center mb-16 ${
            isVisible ? "animate-fade-in-up" : "opacity-0"
          }`}
        >
          <p className="overline text-[#C8A99C] mb-4">Event Rentals</p>
          <h2 className="font-heading text-4xl lg:text-5xl font-light tracking-tight text-[#2C2A28] mb-6">
            Babe Booth
          </h2>
          <div className="section-divider mx-auto mb-6" />
          <p className="font-body text-[#5C5A56] max-w-2xl mx-auto">
            Make your event unforgettable with our photo booth, 360 booth, and audio guest book rentals. Perfect for weddings, parties, and corporate events!
          </p>
        </div>

        {/* Rental Services */}
        <div className="space-y-6">
          {rentalServices.map((service, index) => (
            <div
              key={service.id}
              className={`bg-[#FDFBF9] overflow-hidden transition-all duration-500 ${
                isVisible ? "animate-fade-in-up" : "opacity-0"
              }`}
              style={{ animationDelay: `${index * 100}ms` }}
              data-testid={`rental-${service.id}`}
            >
              {/* Service Header */}
              <button
                onClick={() => toggleService(service.id)}
                className="w-full flex items-center gap-6 p-6 hover:bg-[#F5F2EB]/50 transition-colors"
                data-testid={`rental-toggle-${service.id}`}
              >
                <div className="w-20 h-20 flex-shrink-0 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-grow text-left">
                  <div className="flex items-center gap-3 mb-1">
                    <service.icon size={20} className="text-[#C8A99C]" />
                    <h3 className="font-heading text-2xl text-[#2C2A28]">
                      {service.title}
                    </h3>
                  </div>
                  <p className="font-body text-sm text-[#5C5A56] hidden sm:block">
                    {service.description}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-body text-sm text-[#C8A99C] hidden md:block">
                    {service.packages.length} {service.packages.length === 1 ? 'package' : 'packages'}
                  </span>
                  {expandedService === service.id ? (
                    <ChevronUp size={24} className="text-[#5C5A56]" />
                  ) : (
                    <ChevronDown size={24} className="text-[#5C5A56]" />
                  )}
                </div>
              </button>

              {/* Expanded Packages */}
              {expandedService === service.id && (
                <div className="border-t border-[#E8E4DD] bg-[#FDFBF9] p-6 lg:p-8">
                  <p className="font-body text-[#5C5A56] mb-8 sm:hidden">
                    {service.description}
                  </p>

                  <div className={`grid grid-cols-1 ${service.packages.length === 1 ? 'md:grid-cols-1 max-w-md mx-auto' : service.packages.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-4'} gap-6`}>
                    {service.packages.map((pkg, pkgIndex) => (
                      <div
                        key={pkgIndex}
                        className={`bg-[#F5F2EB] p-6 relative ${
                          pkg.popular ? "ring-2 ring-[#C8A99C]" : ""
                        }`}
                        data-testid={`rental-package-${service.id}-${pkgIndex}`}
                      >
                        {pkg.popular && (
                          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                            <span className="bg-[#C8A99C] text-white text-xs px-3 py-1 font-body uppercase tracking-wider">
                              Popular
                            </span>
                          </div>
                        )}

                        <h4 className="font-heading text-lg text-[#2C2A28] mb-1 mt-2">
                          {pkg.name}
                        </h4>
                        <p className="font-body text-sm text-[#5C5A56] mb-2">
                          {pkg.hours}
                        </p>
                        <p className="font-heading text-3xl text-[#2C2A28] mb-4">
                          {pkg.price}
                        </p>

                        <ul className="space-y-2">
                          {pkg.features.map((feature, featureIndex) => (
                            <li key={featureIndex} className="flex items-start gap-2">
                              <Check size={14} className="text-[#A8B5A3] mt-1 flex-shrink-0" />
                              <span className="font-body text-xs text-[#5C5A56]">
                                {feature}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>

                  {service.note && (
                    <p className="font-body text-xs text-[#5C5A56] mt-6 text-center">
                      * {service.note}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Contact Info */}
        <div
          className={`mt-12 bg-[#FDFBF9] p-8 text-center ${
            isVisible ? "animate-fade-in-up animation-delay-300" : "opacity-0"
          }`}
        >
          <h3 className="font-heading text-2xl text-[#2C2A28] mb-4">
            Ready to Book?
          </h3>
          <p className="font-body text-[#5C5A56] mb-6">
            I would be happy to make a custom package for you if these don't fit your needs!
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <a
              href="tel:337-581-7670"
              className="flex items-center gap-2 font-body text-[#2C2A28] hover:text-[#C8A99C] transition-colors"
              data-testid="babe-booth-phone"
            >
              <Phone size={18} />
              337-581-7670
            </a>
            <a
              href="mailto:babeboothrental@yahoo.com"
              className="flex items-center gap-2 font-body text-[#2C2A28] hover:text-[#C8A99C] transition-colors"
              data-testid="babe-booth-email"
            >
              <Mail size={18} />
              babeboothrental@yahoo.com
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BabeBoothSection;
